import type { StaffFeatureId } from "@wallyo/contracts/actions";
import type { VerificationRejectionReasonCode } from "@wallyo/contracts/console/verifications";
import type { FormEvent } from "react";
import { t } from "../../i18n";
import { formatDateTime, formatTime } from "../../i18n/format";
import { FeatureLock } from "../customers/FeatureLock";
import { Modal, WriteFailureNotice } from "../team/TeamDialogs";
import {
  APPROVE_REASON_MAX_LENGTH,
  REJECT_NOTE_MAX_LENGTH,
  REJECT_REASON_CODES,
  approveBlock,
  describeFailure,
  reasonCodeLabel,
} from "./decision";
import type { DialogEvent, DialogState } from "./dialog-state";
import type { ImagesState } from "./image-viewer";
import {
  STATUS_PILL,
  comparisonRows,
  documentTypeName,
  isOpen,
  sideName,
  tradeOf,
  type VerificationDetail,
} from "./verification-format";
import { verificationsHref, type VerificationFilters } from "./verification-route";

export const VIEW_ID_IMAGES: StaffFeatureId = "VIEW_ID_IMAGES_STAFF_FEATURE_ENABLEMENT";

/** What the server said after a decision: shown once it answered, never before. */
export type Outcome = { kind: "approve" } | { kind: "reject" };

export type DetailViewProps = {
  verification: VerificationDetail;
  filters: VerificationFilters;
  images: ImagesState;
  canApprove: boolean;
  roles: readonly string[];
  dialog: DialogState | null;
  outcome: Outcome | null;
  onShowImages: () => void;
  onHideImages: () => void;
  onDialog: (event: DialogEvent) => void;
  onReload: () => void;
  now?: Date;
};

/** One verification, as in the prototype: who, the documents, read against typed, then the decision. */
export function DetailView(props: DetailViewProps) {
  const { verification: v, filters } = props;
  const pill = STATUS_PILL[v.status];
  return (
    <div className="card verif-detail" data-verification={v.identityVerificationId}>
      <div className="verif-head">
        <div>
          <h3>
            {v.pro.name} · {tradeOf(v.pro.category)}
          </h3>
          <p className="meta">
            {[
              v.pro.city,
              v.pro.maskedPhone ?? t("verifications.detail.noPhone"),
              `${documentTypeName(v.documentType)} (${v.country})`,
              t("verifications.detail.submitted", { date: formatDateTime(v.submittedAt) }),
            ].join(" · ")}
          </p>
        </div>
        <span className="verif-pills">
          <span className={`pill ${pill.tone}`}>{t(pill.label)}</span>
          <span className="meta mono" title={t("verifications.detail.versionTitle")}>
            v{v.version}
          </span>
        </span>
      </div>

      {v.bannedDocument || v.matchesBannedDocument ? <BannedAlert {...props} /> : null}
      <Outcomes {...props} />
      <Images {...props} />

      <div className="table-wrap">
        <table className="match">
          <thead>
            <tr>
              <th>{t("verifications.col.field")}</th>
              <th>{t("verifications.col.read")}</th>
              <th>{t("verifications.col.typed")}</th>
              <th aria-label={t("verifications.col.match")} />
            </tr>
          </thead>
          <tbody>
            {comparisonRows(v, props.now).map((row) => (
              <tr key={row.id} data-row={row.id}>
                <td>{t(row.field)}</td>
                <td className={row.mono ? "mono" : undefined}>{row.read ?? "—"}</td>
                <td className={row.mono ? "mono" : undefined}>{row.typed ?? t("verifications.notAsked")}</td>
                <td>
                  <span className={`pill ${row.pill.tone}`}>{t(row.pill.label)}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="foot">{t("verifications.last4Hint", { last4: v.documentNumberLast4 })}</p>

      <div className="verif-facts">
        <div>
          <h2 className="verif-h">{t("verifications.standing.title")}</h2>
          <ul className="kv">
            <li>
              <span>{t("verifications.standing.verificationStatus")}</span>
              <span>{t(`verifications.standing.status.${v.proStanding.verificationStatus}` as const)}</span>
            </li>
            <li>
              <span>{t("verifications.standing.approvalStatus")}</span>
              <span>{t(`verifications.standing.approval.${v.proStanding.approvalStatus}` as const)}</span>
            </li>
            <li>
              <span>{t("verifications.standing.isVerified")}</span>
              <span>{v.proStanding.isVerified ? t("verifications.yes") : t("verifications.no")}</span>
            </li>
          </ul>
        </div>
        <div>
          <h2 className="verif-h">{t("verifications.previous.title")}</h2>
          {v.previousAttempts.length === 0 ? (
            <p className="foot">{t("verifications.previous.none")}</p>
          ) : (
            <ul className="kv" data-previous="">
              {v.previousAttempts.map((attempt) => (
                <li key={attempt.identityVerificationId}>
                  <a className="go-link" href={verificationsHref(filters, attempt.identityVerificationId)}>
                    {formatDateTime(attempt.submittedAt)}
                  </a>
                  <span className={`pill ${STATUS_PILL[attempt.status].tone}`}>
                    {t(STATUS_PILL[attempt.status].label)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {isOpen(v.status) ? <Actions {...props} /> : <Decided {...props} />}
      {props.dialog ? <DecisionDialog {...props} dialog={props.dialog} /> : null}
    </div>
  );
}

function BannedAlert({ verification: v }: DetailViewProps) {
  return (
    <div className="verif-banned" role="alert">
      <b>{t("verifications.banned.title")}</b>
      <span>
        {v.bannedDocument
          ? t("verifications.banned.body", {
              reason: v.bannedDocument.reason,
              date: formatDateTime(v.bannedDocument.bannedAt),
            })
          : t("verifications.banned.bodyNoReason")}
      </span>
      <span className="meta">{t("verifications.banned.notTold")}</span>
    </div>
  );
}

function Outcomes({ outcome, verification: v }: DetailViewProps) {
  if (!outcome) return null;
  if (outcome.kind === "approve") {
    return (
      <div className="banner ok" role="status">
        {t("verifications.outcome.approved", { name: v.pro.name })}
      </div>
    );
  }
  return (
    <div className="banner ok" role="status">
      {t("verifications.outcome.rejected")}
    </div>
  );
}

function Images({ verification: v, images, roles, onShowImages, onHideImages }: DetailViewProps) {
  if (images.status === "locked") {
    return <FeatureLock lead={t("verifications.images.locked")} feature={VIEW_ID_IMAGES} roles={roles} />;
  }
  if (v.images.deletedAt) {
    return (
      <p className="foot verif-images-note">
        {t("verifications.images.deleted", { date: formatDateTime(v.images.deletedAt) })}
      </p>
    );
  }
  const sides = v.images.sides;
  if (images.status === "shown") {
    return (
      <section className="verif-images" aria-label={t("verifications.images.aria")}>
        <div className="docs">
          {images.images.map((image) => (
            <figure className="doc shown" key={image.side}>
              <img
                src={image.signedUrl}
                alt={sideName(image.side)}
                referrerPolicy="no-referrer"
                draggable={false}
                decoding="async"
                // Also keeps a server render from adding a <link rel="preload"> with the URL.
                loading="lazy"
              />
              <figcaption className="meta">{sideName(image.side)}</figcaption>
            </figure>
          ))}
        </div>
        <div className="verif-images-bar">
          <span className="meta">{t("verifications.images.recorded", { time: formatTime(images.expiresAt) })}</span>
          <button className="btn quiet small" type="button" onClick={onHideImages}>
            {t("verifications.images.hide")}
          </button>
        </div>
      </section>
    );
  }
  const error = images.status === "hidden" ? images.error : null;
  const failure = error ? describeFailure(error, "images") : null;
  return (
    <section className="verif-images" aria-label={t("verifications.images.aria")}>
      <div className="docs">
        {sides.map((side) => (
          <div className="doc" key={side}>
            {images.status === "loading" ? <span className="spinner" aria-hidden="true" /> : sideName(side)}
          </div>
        ))}
      </div>
      {images.status === "expired" ? <p className="foot">{t("verifications.images.expired")}</p> : null}
      {failure ? <WriteFailureNotice failure={failure} /> : null}
      <div className="verif-images-bar">
        <span className="meta">{t("verifications.images.notice")}</span>
        <button className="btn" type="button" onClick={onShowImages} disabled={images.status === "loading"}>
          {images.status === "loading"
            ? t("verifications.images.loading")
            : images.status === "expired"
              ? t("verifications.images.showAgain")
              : t("verifications.images.show")}
        </button>
      </div>
    </section>
  );
}

function Actions({ verification: v, canApprove, onDialog }: DetailViewProps) {
  const block = approveBlock(v, canApprove);
  return (
    <div className="verif-actions">
      <div className="btns">
        <button
          className="btn primary"
          type="button"
          disabled={block !== null}
          title={block ? t(block) : undefined}
          onClick={() => onDialog({ type: "open", kind: "approve" })}
        >
          {t("verifications.approve.button")}
        </button>
        <button className="btn danger" type="button" onClick={() => onDialog({ type: "open", kind: "reject" })}>
          {t("verifications.reject.button")}
        </button>
      </div>
      {block ? (
        <p className="foot" data-approve-block="">
          {t(block)}
        </p>
      ) : null}
    </div>
  );
}

function Decided({ verification: v }: DetailViewProps) {
  const by = v.reviewedBy?.name ?? "—";
  const when = v.reviewedAt ? formatDateTime(v.reviewedAt) : "—";
  return (
    <div className="verif-decided">
      <p className="foot">
        {v.status === "approved"
          ? t("verifications.decided.approved", { name: by, date: when })
          : t("verifications.decided.rejected", { name: by, date: when })}
      </p>
      {v.rejectionReasonCode ? (
        <p className="foot">
          {t("verifications.decided.reasonCode", { reason: reasonCodeLabel(v.rejectionReasonCode) })}
        </p>
      ) : null}
      {v.rejectionReason ? (
        <p className="foot">{t("verifications.decided.reason", { reason: v.rejectionReason })}</p>
      ) : null}
    </div>
  );
}

function DecisionDialog({ verification: v, dialog, onDialog, onReload }: DetailViewProps & { dialog: DialogState }) {
  const reject = dialog.kind === "reject";
  const max = reject ? REJECT_NOTE_MAX_LENGTH : APPROVE_REASON_MAX_LENGTH;
  const failure = dialog.error ? describeFailure(dialog.error, dialog.kind) : null;
  const close = () => onDialog({ type: "close" });
  const submit = (event: FormEvent) => {
    event.preventDefault();
    onDialog({ type: "submit" });
  };
  return (
    <Modal
      title={t(reject ? "verifications.reject.title" : "verifications.approve.title", { name: v.pro.name })}
      onClose={close}
    >
      <form className="modal-form" onSubmit={submit} noValidate>
        <div className="modal-body">
          <p>{t(reject ? "verifications.reject.body" : "verifications.approve.body")}</p>
          <p className="meta">{t("verifications.dialog.version", { version: v.version })}</p>
        </div>
        {reject ? (
          <div className="field">
            <label htmlFor="verif-reason-code">{t("verifications.reject.preset")}</label>
            <select
              id="verif-reason-code"
              value={dialog.reasonCode ?? ""}
              disabled={dialog.pending}
              aria-invalid={dialog.problem && dialog.reasonCode === null ? true : undefined}
              onChange={(event) =>
                onDialog({
                  type: "reasonCode",
                  reasonCode: (event.target.value || null) as VerificationRejectionReasonCode | null,
                })
              }
            >
              <option value="">{t("verifications.reject.presetNone")}</option>
              {REJECT_REASON_CODES.map((code) => (
                <option key={code} value={code}>
                  {reasonCodeLabel(code)}
                </option>
              ))}
            </select>
          </div>
        ) : null}
        <div className="field">
          <label htmlFor="verif-reason">
            {t(reject ? "verifications.reject.reason" : "verifications.approve.reason")}
          </label>
          <textarea
            id="verif-reason"
            rows={reject ? 3 : 2}
            value={dialog.reason}
            maxLength={max}
            disabled={dialog.pending}
            aria-invalid={dialog.problem ? true : undefined}
            aria-describedby="verif-reason-hint"
            onChange={(event) => onDialog({ type: "reason", reason: event.target.value })}
          />
          <span className="meta" id="verif-reason-hint">
            {reject ? t("verifications.reject.noteHint") : t("verifications.approve.hint")}{" "}
            <span data-count="">{t("verifications.reason.count", { count: dialog.reason.trim().length, max })}</span>
          </span>
        </div>
        {dialog.problem ? <p className="field-problem">{dialog.problem}</p> : null}
        {failure ? <WriteFailureNotice failure={failure} onReload={onReload} /> : null}
        <div className="btns modal-actions">
          <button className="btn quiet" type="button" onClick={close} disabled={dialog.pending}>
            {t("verifications.dialog.cancel")}
          </button>
          <button className={`btn ${reject ? "danger" : "primary"}`} type="submit" disabled={dialog.pending}>
            {dialog.pending
              ? t("verifications.dialog.sending")
              : t(reject ? "verifications.reject.submit" : "verifications.approve.submit")}
          </button>
        </div>
      </form>
    </Modal>
  );
}
